import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../_shared/hooks';
import { launchDispatch } from '../api-client';
import type { WaterfallRow } from '../components/waterfallTypes';
import type { StatusMapping, Campaign } from '../components/types';
import { isUnconfirmed, settleWrite } from './unconfirmed-write';

interface LaunchDispatchPayload {
  campaignId: string;
  waterfall_rows: WaterfallRow[];
  status_mapping: StatusMapping;
  direct?: boolean;
}

/**
 * Resolves to the dispatch summary, or to `{ unconfirmed: true }` when the launch
 * may have gone out but the response was unreadable or cut off (120 s timeout).
 * Never rejects for that case: a retry would start a second dispatch wave, so the
 * caller keeps its Launch buttons locked and points the user at the dispatch log.
 */
export function useLaunchDispatch() {
  const { apiFetch } = useApi();
  const queryClient = useQueryClient();
  const refresh = (campaignId: string) =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId] }),
      queryClient.invalidateQueries({ queryKey: ['launcher', 'campaigns', campaignId, 'dispatch-log'] }),
    ]);
  return useMutation({
    mutationFn: ({ campaignId, ...payload }: LaunchDispatchPayload) =>
      settleWrite(() => launchDispatch(apiFetch, campaignId, payload), () => refresh(campaignId)),
    onSuccess: (data, { campaignId }) => {
      if (isUnconfirmed(data)) return;
      queryClient.setQueryData<Campaign>(
        ['launcher', 'campaigns', campaignId],
        old => old ? { ...old, launcher_status: 'launched' } : old,
      );
      void refresh(campaignId);
    },
  });
}
